import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Parse date string (YYYY-MM-DD) to local date without timezone issues.
 * When you use new Date("2024-12-14"), it's interpreted as UTC midnight,
 * which becomes the previous day in timezones behind UTC (like PST).
 * This function creates a date in the local timezone instead.
 * 
 * IMPORTANT: Using new Date(year, month - 1, day) creates a date in LOCAL timezone
 * at midnight, which ensures getDate() returns the correct day number.
 */
export function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  
  // Validate inputs
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    console.error('[parseLocalDate] Invalid date string:', dateString);
    return new Date(dateString); // Fallback to default parsing
  }
  
  // Create date directly in local timezone (month is 0-indexed)
  // This creates the date at midnight LOCAL time, avoiding UTC interpretation
  const date = new Date(year, month - 1, day);
  
  // Verify the date components match what we expect
  if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
    console.error('[parseLocalDate] Date component mismatch:', {
      input: { year, month, day },
      output: { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() },
      dateString,
      dateStringified: date.toString(),
      dateISO: date.toISOString(),
    });
  }
  
  return date;
}

/**
 * Get today's date in Pacific Time (Las Vegas, NV timezone) as YYYY-MM-DD string.
 *
 * IMPORTANT: Do NOT use new Date().toISOString().split("T")[0] for date comparisons!
 * That converts to UTC first, so after 4 PM Pacific, it returns tomorrow's date.
 * This function returns the correct local date for the restaurant's timezone.
 */
export function getTodayLocal(): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return formatter.format(new Date()); // Returns "YYYY-MM-DD"
}
